import React, { useState, useEffect} from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import {
  Box,
  Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import dayjs from 'dayjs';

import { WhiteBox } from 'components/AppShell';
import { CopyButton } from 'components/CustomButtons';
import { AttemptRateChart } from 'components/Analytics/ScoreDistribution';
import { getShortlistTaskById } from 'services/shortlistingTask';
import { getShortlistTaskAverageAndAttemptRate } from 'services/analyticsV2';
import ShortlistScoreList from 'components/ShortlistScoreList';
import Chip from 'components/Chip';
import ScreenHeading from 'components/ScreenHeading';
import ScoreDistributionChart from 'components/Analytics/ScoreDistribution';

const useStyles = makeStyles((theme) => ({
  boxContainer: {
    display: "flex",
    flexDirection: "column",
    minHeight: 'calc(100vh - 56px)',
    maxWidth: 'calc(100% - 8px)',
    paddingBottom: theme.spacing(6),
    gap: theme.spacing(5)
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5)
  },
  taskId: {
    display: 'flex',
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 3),
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  leftSection: {
    width: '50%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column'
  },
  metricsContainer: {
    display: 'flex',
    gap: '16px',
    width: '100%'
  },
  chartContainer: {
    display: 'flex',
    gap: '70px',
    padding: '20px',
  }
}));

const TaskHeader = ({ task, theme, classes }) => {
  if (!task) return null;

  return (
    <Box className={classes.header}>
      <Box display="flex" flexDirection="column" gap="12px">
        <Typography variant="h4-medium">{task.name}</Typography>
        
        <Box display="flex" justifyContent="space-between">
          <TaskChips task={task} theme={theme} />
          <TaskIdSection task={task} classes={classes} />
        </Box>
        
        <Box
          sx={{ 
            // marginTop: '4px', 
            borderTop: `1px solid ${theme.palette.neutral['clr-200']}` 
          }} 
          
        />
        
        <TaskDetails task={task} />
      </Box>
    </Box>
  );
};

const TaskChips = ({ task, theme }) => (
  <Box display="flex" gap="4px">
    <Chip
      content="Shortlisting task"
      color={theme.palette.primary.main}
      bgColor={theme.palette.primary['clr-50']}
      sx={{ borderRadius: theme.spacing(1) , display : 'flex' , alignItems : 'center' }}
    />
    <Chip
      content={task?.company ? "Company specific" : "Job role specific"}
      color={theme.palette.primary.main}
      bgColor={theme.palette.primary['clr-50']}
      sx={{ borderRadius: theme.spacing(1) , display : 'flex' , alignItems : 'center'}}
    />
  </Box>
);

const TaskIdSection = ({ task, classes }) => (
  <Box className={classes.taskId}>
    <Typography variant="body01-bold">Task ID:</Typography>
    <Typography variant="body01-bold" color="neutral.clr-600">
      {task?._id}
    </Typography>
    <CopyButton
      content={task?._id}
      title="copy"
      iconProps={{ color: "primary", fontSize: '10px !important' }}
      size="small"
    />
  </Box>
);

const TaskDetails = ({ task }) => {
  const expiresOn = task?.expiresOn;
  const isNeverExpires = expiresOn && dayjs(expiresOn).year() === 9999;
  
  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h6-medium">
          Batch: <span style={{ color: '#4E5661' }}>{task?.batch?.name}</span>
        </Typography>
        <Typography variant="h6-medium">
          Published on: <span style={{ color: '#4E5661' }}>
            {dayjs(task?.publishOn).format('DD MMMM YYYY')}
          </span>
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h6-medium">
          Attempted by: <span style={{ color: '#4E5661' }}>
            {task?.attemptedBy.length} students
          </span>
        </Typography>
        <Typography variant="h6-medium" color="danger.clr-500">
          {isNeverExpires ? 'Never expires' : `Expires on: ${dayjs(task?.expiresOn).format('DD MMMM YYYY')}`}
        </Typography>
      </Box>
    </Box>
  );
};

const ShortlistingPerformance = () => {
  const classes = useStyles();
  const { id } = useParams();
  const theme = useTheme();
  
  const [task, setTask] = useState(null);
  const [taskLoading, setTaskLoading] = useState(true);
  const [taskError, setTaskError] = useState(null);

  const [attemptRateData, setAttemptRateData] = useState(null);
  const [attemptRateLoading, setAttemptRateLoading] = useState(true);
  const [attemptRateError, setAttemptRateError] = useState(null);

  useEffect(() => {
    if (!id) return;
    setTaskLoading(true);
    setTaskError(null);
    getShortlistTaskById(id)
      .then(response => setTask(response))
      .catch(err => setTaskError(err.message))
      .finally(() => setTaskLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setAttemptRateLoading(true);
    setAttemptRateError(null);
    getShortlistTaskAverageAndAttemptRate(id)
      .then(response => setAttemptRateData(response))
      .catch(err => setAttemptRateError(err.message))
      .finally(() => setAttemptRateLoading(false));
  }, [id]);

  if (taskLoading || attemptRateLoading) {
    return <Box>Loading...</Box>;
  }

  if (taskError || attemptRateError) {
    return <Box>Error loading data. Please try again.</Box>;
  }

  return (
    <Box className={classes.boxContainer}>
      <ScreenHeading title="Shortlisting Genie" id="shortlisting-genie" />
      
      <WhiteBox>
        <TaskHeader task={task} theme={theme} classes={classes} />
      </WhiteBox>
      
      <Box className={classes.metricsContainer}>
        <WhiteBox>
          <ScoreDistributionChart
            avgDistribution={{
              batchAverageScore: Math.round(attemptRateData?.averageScoreStats?.averageScore || 0)
            }}
            scoreDistribution={attemptRateData?.averageScoreStats || {"0-30": 0, "31-60": 0, "61-100": 0}}
            totalStudents={attemptRateData?.totalStudents || 0}
            classes={classes}
            interviewVariantType="assessment"
          />
        </WhiteBox>
        <WhiteBox>
          <AttemptRateChart
            attemptRate={{
              attemptRate: attemptRateData?.attemptRateStats?.attemptRate || 0,
              userSubmittedCount: (attemptRateData?.attemptRateStats?.["31-60"] || 0) + (attemptRateData?.attemptRateStats?.["61-100"] || 0),
              userNotSubmittedCount: attemptRateData?.attemptRateStats?.["0-30"] || 0
            }}
            classes={classes}
            totalStudents={attemptRateData?.totalStudents || 0}
            isShortlisting={true}
          />
        </WhiteBox>
      </Box>

      <ShortlistScoreList shortlistId={id}/>
    </Box>
  );
};

export default ShortlistingPerformance;